/*
  Sticky add to cart — the bar pinned to the bottom of the viewport once the real buy row has
  scrolled off the top of the screen.

  Two rules the whole file is built around:

  - It owns no state. Price, button label, disabled, quantity and the chosen options are all read
    back out of the live DOM, and every control forwards its click to the real one. product-info.js,
    pi-bundle.js, pi-variant-guard.js and the back-in-stock app carry on exactly as they did; none
    of them has to know this exists. In particular the button clicks the real submit rather than
    posting a form of its own, so the bundle line item payload, the error message target and the
    cart drawer all stay in one place.

  - It re-reads its targets on every sync. product-info.js swaps the innerHTML of #price-,
    #Quantity-Form- and friends on each variant change, so any node cached at startup is stale the
    moment the shopper touches an option. Nothing here is looked up once.
*/
if (!customElements.get('pi-sticky-atc')) {
  customElements.define(
    'pi-sticky-atc',
    class PiStickyAtc extends HTMLElement {
      connectedCallback() {
        this.root = this.closest('product-info');

        /*
          Quick add renders the same <product-info> markup inside a modal, which would give the
          popup a second, page-wide fixed bar over the top of it. The bar belongs to the product
          page only.
        */
        if (!this.root || this.closest('quick-add-modal')) return;

        this.visible = false;
        this.passed = false;
        this.atFooter = false;
        this.buyRow = null;
        this.frame = 0;
        this.needsSync = false;

        this.ui = {
          price: this.querySelector('[data-sticky-price]'),
          compare: this.querySelector('[data-sticky-compare]'),
          variant: this.querySelector('[data-sticky-variant]'),
          label: this.querySelector('[data-sticky-label]'),
          submit: this.querySelector('[data-sticky-submit]'),
          minus: this.querySelector('[data-sticky-qty="minus"]'),
          plus: this.querySelector('[data-sticky-qty="plus"]'),
          qtyValue: this.querySelector('[data-sticky-qty-value]'),
          thumb: this.querySelector('[data-sticky-thumb]'),
        };

        this.onClick = this.onClick.bind(this);
        this.onScroll = this.request.bind(this, false);
        this.onChange = this.request.bind(this, true);

        this.addEventListener('click', this.onClick);

        /*
          Geometry comes from a rAF-throttled scroll listener rather than an IntersectionObserver.
          An observer looks like the right tool and is not: it only reports threshold crossings, and
          the buy row being below the viewport and being above it are both "not intersecting". Jump
          to the bottom of the page — a tab link, a restored scroll position, a back-to-top — and the
          state changes without ever crossing a threshold, so no callback arrives and the bar stays
          down for the rest of the visit. One getBoundingClientRect per animation frame while the
          page is actually moving is the cheaper mistake.
        */
        window.addEventListener('scroll', this.onScroll, { passive: true });
        window.addEventListener('resize', this.onScroll, { passive: true });

        /*
          Dawn's <quantity-input> assigns input.value, which mutates a property and no attribute, so
          a MutationObserver never sees it. The events it fires afterwards bubble, so the row itself
          is where the quantity (and a native select fallback) is picked up.
        */
        this.root.addEventListener('change', this.onChange);
        this.root.addEventListener('input', this.onChange);

        /*
          Three observers rather than one on <product-info>. The bar is itself a child of that
          element, so a single subtree observer would see this element's own writes and re-run the
          sync that produced them, every frame. Watching the .page-width column instead keeps the
          bar outside what is observed, and the two narrow attribute observers pick up the only
          things left that matter: pi-info--variant-unavailable on the section, and the body class
          the cart drawer and media modal set while they are open.
        */
        this.contentObserver = new MutationObserver(this.onChange);
        const column = this.root.querySelector('.page-width');
        if (column) {
          this.contentObserver.observe(column, {
            childList: true,
            subtree: true,
            characterData: true,
            attributes: true,
          });
        }

        this.sectionObserver = new MutationObserver(this.onChange);
        this.sectionObserver.observe(this.root, { attributes: true, attributeFilter: ['class'] });

        this.bodyObserver = new MutationObserver(this.onChange);
        this.bodyObserver.observe(document.body, { attributes: true, attributeFilter: ['class'] });

        this.sync();
      }

      disconnectedCallback() {
        cancelAnimationFrame(this.frame);
        this.contentObserver?.disconnect();
        this.sectionObserver?.disconnect();
        this.bodyObserver?.disconnect();
        window.removeEventListener('scroll', this.onScroll);
        window.removeEventListener('resize', this.onScroll);
        this.root?.removeEventListener('change', this.onChange);
        this.root?.removeEventListener('input', this.onChange);
        this.removeEventListener('click', this.onClick);
      }

      /*
        One frame of work at most, whatever arrives. Scrolling only needs the measurement; a
        mutation or a change event needs the full re-read, and those come in bursts — a variant
        change rewrites several blocks in a row — so the flag survives until the frame runs.
      */
      request(full) {
        if (full) this.needsSync = true;
        if (this.frame) return;
        this.frame = requestAnimationFrame(() => {
          this.frame = 0;
          if (this.needsSync) {
            this.needsSync = false;
            this.sync();
          } else {
            this.measure();
            this.render();
          }
        });
      }

      /*
        The footer, or whatever section group sits in its place. Looked up lazily and re-looked-up
        once it leaves the document, which is what the theme editor does to a section it rebuilds.
      */
      get footer() {
        if (!this.footerEl?.isConnected) {
          this.footerEl =
            document.querySelector('.shopify-section-group-footer-group') || document.querySelector('footer');
        }
        return this.footerEl;
      }

      /*
        Two questions, both answered from geometry.

        Passed: fully above the viewport, not merely leaving it. While the real button is still
        partly on screen it is the one to use, and before the shopper has reached it a duplicate
        laid over copy they have not read yet is noise; past it, this bar is the only way back. A
        sold-out row is display:none and measures zero, which the height check keeps out.

        At the footer: the bar stands down as soon as the footer's top edge enters the viewport. By
        then the shopper has scrolled past the tabs, the FAQ and the related products — they are
        leaving rather than deciding — and the footer is full of things they went there for. The
        test is the viewport edge and not the bar's own top, because the bar is flush with the
        bottom: the instant any footer is on screen it is behind the bar. Measuring against the
        bar's height instead lets it sit on the footer for a whole bar-height of scrolling first.
        Nothing here moves the footer, so there is no threshold to oscillate around.
      */
      measure() {
        const rect = this.buyRow?.getBoundingClientRect();
        this.passed = !!rect && rect.height > 0 && rect.bottom <= 0;

        const footer = this.footer?.getBoundingClientRect();
        this.atFooter = !!footer && footer.top < window.innerHeight;
      }

      /* textContent minus the screen-reader-only spans Dawn puts inside the price and the button. */
      static visibleText(node) {
        return Array.from(node.childNodes)
          .filter((child) => !(child.nodeType === 1 && child.classList.contains('visually-hidden')))
          .map((child) => child.textContent)
          .join('')
          .trim();
      }

      static setText(node, text) {
        if (node && node.textContent !== text) node.textContent = text;
      }

      /*
        One span per chosen product, so the separator between them belongs to the stylesheet rather
        than to the string. A bundle needs that mark to carry real weight — the values themselves
        contain "/" already — and CSS can give a pseudo-element its own size and colour in a way a
        character baked into the text never gets. Built with createElement and textContent: this
        copy is merchant data coming back out of the DOM, and it is never worth an innerHTML.
      */
      static setOptions(node, parts) {
        if (!node) return;

        const key = parts.join('');
        if (node.dataset.options === key) return;
        node.dataset.options = key;

        node.textContent = '';
        parts.forEach((part) => {
          const item = document.createElement('span');
          item.className = 'pi-sticky__variant-item';
          item.textContent = part;
          node.appendChild(item);
        });

        /* The line ellipses on a narrow viewport, so the whole of it stays reachable on hover. */
        node.title = parts.join(' · ');
      }

      /*
        The chosen value of every option dropdown under `scope`, read off the upgraded trigger and
        falling back to the native select for the moment before pi-variant-dropdown.js connects.
        `skipBundle` leaves out the dropdowns that belong to a bundled product rather than to the
        product being viewed — those are collected per row instead, see sync().
      */
      static readOptions(scope, skipBundle) {
        return Array.from(scope.querySelectorAll('.pi-variants__field'))
          .filter((field) => !skipBundle || !field.closest('.pi-bundle'))
          .map((field) => {
            const trigger = field.querySelector('.pi-variants__value');
            if (trigger?.textContent.trim()) return trigger.textContent.trim();
            return field.querySelector('select')?.selectedOptions[0]?.textContent.trim() || '';
          })
          .filter(Boolean);
      }

      sync() {
        const { root, ui } = this;
        if (!root) return;

        this.buyRow = root.querySelector('.pi-buy');
        this.atc = root.querySelector('.pi-atc');

        const amount = root.querySelector('.pi-price__amount');
        PiStickyAtc.setText(ui.price, amount ? PiStickyAtc.visibleText(amount) : '');

        const compare = root.querySelector('.pi-price__compare');
        const compareText = compare && !compare.hasAttribute('hidden') ? PiStickyAtc.visibleText(compare) : '';
        PiStickyAtc.setText(ui.compare, compareText);
        ui.compare?.toggleAttribute('hidden', compareText === '');

        /*
          A bundle's options are not the viewed product's — they sit one row per bundled item in the
          ticket panel, and the parent product carries none of its own. Each row becomes one part,
          its own fields joined with "/" the way a single product's are, and setOptions draws the
          separator between parts. A single product is one part and therefore gets no separator at
          all: its "/" already says these belong to the same thing.
        */
        const bundleRows = root.querySelectorAll('.pi-bundle__panel .pi-variants__group');
        const parts = bundleRows.length
          ? Array.from(bundleRows).map((row) => PiStickyAtc.readOptions(row).join(' / '))
          : [PiStickyAtc.readOptions(root, true).join(' / ')];

        PiStickyAtc.setOptions(ui.variant, parts.filter(Boolean));

        /* A single-variant product has no picker to jump to, so the strip drops its affordance. */
        this.classList.toggle('pi-sticky--jumpable', !!root.querySelector('.pi-variants'));

        const label = this.atc?.querySelector(':scope > span');
        if (label) PiStickyAtc.setText(ui.label, PiStickyAtc.visibleText(label));
        if (ui.submit) {
          ui.submit.disabled = !this.atc || this.atc.hasAttribute('disabled');
          ui.submit.classList.toggle('is-loading', !!this.atc?.classList.contains('loading'));
        }

        const input = root.querySelector('.pi-qty__input');
        PiStickyAtc.setText(ui.qtyValue, input ? input.value : '');
        const minus = root.querySelector('.pi-qty__button[name="minus"]');
        const plus = root.querySelector('.pi-qty__button[name="plus"]');
        if (ui.minus) ui.minus.disabled = !minus || minus.classList.contains('disabled');
        if (ui.plus) ui.plus.disabled = !plus || plus.classList.contains('disabled');

        /* Follow the gallery rather than the product's featured image, so a variant swap or a
           swipe leaves the bar showing the shot the shopper is actually looking at. */
        const active = root.querySelector('.pi-gallery__media-item.is-active .pi-gallery__img');
        const src = active?.currentSrc || active?.getAttribute('src');
        if (ui.thumb && src && ui.thumb.getAttribute('src') !== src) ui.thumb.setAttribute('src', src);

        this.measure();
        this.render();
      }

      render() {
        const covered = document.body.classList.contains('overflow-hidden');
        const unavailable = this.root.classList.contains('pi-info--variant-unavailable');
        const show = this.passed && !this.atFooter && !covered && !unavailable && !!this.atc;

        if (show === this.visible) return;
        this.visible = show;

        this.classList.toggle('is-visible', show);
        this.setAttribute('aria-hidden', show ? 'false' : 'true');
        this.toggleAttribute('inert', !show);
      }

      onClick(event) {
        const step = event.target.closest('[data-sticky-qty]');
        if (step) {
          /* Forward to the real stepper so Dawn's min / max / step and the per-variant cart
             quantity rules decide the new value. This bar never writes the input itself. */
          this.root.querySelector(`.pi-qty__button[name="${step.dataset.stickyQty}"]`)?.click();
          return;
        }

        if (event.target.closest('[data-sticky-submit]')) {
          this.root.querySelector('.pi-atc')?.click();
          return;
        }

        if (event.target.closest('[data-sticky-jump]')) {
          /*
            Back up to the picker. `center` rather than `start` so the sticky header cannot land on
            top of the field — no header height to read, and it holds whatever the header is doing.
          */
          const picker = this.root.querySelector('.pi-variants');
          picker?.scrollIntoView({
            behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
            block: 'center',
          });
        }
      }
    }
  );
}
